import { useState } from 'react';
import { Link } from 'react-router-dom';

interface MomentItem {
  number: string;
  title: string;
  question: string;
  description: string;
}

const items: MomentItem[] = [
  {
    number: '01',
    title: 'Nuevo rol',
    question: '¿Cómo puedo construir una presencia de liderazgo clara y confiable?',
    description:
      'Asumiste una responsabilidad mayor y necesitas realizar una transición consciente de colaborador a líder efectivo.',
  },
  {
    number: '02',
    title: 'Decisión relevante',
    question: '¿Cómo puedo tomar una decisión importante con mayor claridad?',
    description:
      'Tienes diferentes opciones, pero necesitas ordenar el ruido, analizar el contexto y evaluar sus implicaciones reales.',
  },
  {
    number: '03',
    title: 'Cambio organizacional',
    question: '¿Cómo puedo guiar a mi equipo durante un proceso de cambio?',
    description:
      'Necesitas acompañar a tu equipo del estado actual a uno deseado sin perder dirección, claridad ni confianza.',
  },
  {
    number: '04',
    title: 'Bloqueo profesional',
    question: '¿Cómo puedo recuperar intención y movimiento en mi carrera?',
    description:
      'Has logrado mucho, pero algo dejó de ser satisfactorio y necesitas reconsiderar conscientemente el siguiente paso.',
  },
];

export default function MomentoActual() {
  const [openIndex, setOpenIndex] = useState<number>(0);

  return (
    <section id="momento-actual" className="relative bg-background-100 py-20 md:py-28 lg:py-32">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-24">
          <div className="lg:col-span-7 reveal-on-scroll">
            <p className="text-[11px] md:text-xs tracking-[0.28em] uppercase text-accent-600 mb-6">
              Cuando algo cambia
            </p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-[56px] leading-[1.05] font-light text-foreground-900">
              Hay momentos profesionales que piden una mirada más profunda.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:flex lg:flex-col lg:justify-end reveal-on-scroll">
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed">
              La mentoría estratégica puede ayudarte cuando una nueva responsabilidad, una
              retroalimentación inesperada o una decisión importante interrumpe la rutina y te
              obliga a reconsiderar el siguiente paso.
            </p>
          </div>
        </div>

        {/* Subheadline */}
        <p className="reveal-on-scroll font-heading text-2xl md:text-3xl font-light text-foreground-800 mb-12 md:mb-16">
          ¿Cómo puedo ayudarte?
        </p>

        {/* Accordion */}
        <div className="divide-y divide-background-200/70 border-y border-background-200/70">
          {items.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div key={item.number} className="reveal-on-scroll">
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center gap-5 md:gap-8 py-7 md:py-8 text-left cursor-pointer group"
                >
                  <span className="font-heading text-3xl md:text-5xl font-light text-accent-400/80 flex-shrink-0 w-14 md:w-20">
                    {item.number}
                  </span>
                  <span className="flex-1 font-heading text-2xl md:text-4xl font-light text-foreground-900 tracking-[0.01em]">
                    {item.title}
                  </span>
                  <span className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-full border border-background-300/70 text-foreground-600 transition-all duration-500 group-hover:border-accent-300 group-hover:text-accent-600">
                    <i
                      className={`ri-add-line text-xl transition-transform duration-500 ${
                        isOpen ? 'rotate-45' : ''
                      }`}
                    ></i>
                  </span>
                </button>

                <div
                  className={`grid transition-all duration-500 ease-out ${
                    isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="pb-8 md:pb-10 pl-[76px] md:pl-[112px] pr-4 max-w-[760px]">
                      <p className="font-heading text-xl md:text-2xl italic font-light text-foreground-800 mb-3">
                        {item.question}
                      </p>
                      <p className="text-foreground-600 text-base leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-12 reveal-on-scroll">
          <Link
            to="/servicios"
            className="inline-block px-7 py-3.5 rounded-full text-sm font-medium tracking-[0.03em] whitespace-nowrap transition-all duration-300 cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600"
          >
            Explora los servicios
          </Link>
        </div>
      </div>
    </section>
  );
}